import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, FaCheckCircle, FaShieldAlt, FaMapMarkerAlt, 
  FaStar, FaUser, FaEnvelope, FaPhone, FaLock, FaWallet, 
  FaRegCalendarAlt, FaChevronRight 
} from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import "./Checkout.css";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = localStorage.getItem("guestUserId");

  // Extracting data from state
  const { hotel, room, customer, sessionId, searchDetails } = location.state || {};

  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Safety check: If essential data is missing, redirect back
    if (!hotel || !room || !searchDetails) {
        navigate("/");
        return;
    }

    if (userId) {
      axios.get(`${baseurl}/getWalletBalance/${userId}`)
        .then(res => setWalletBalance(parseFloat(res.data.balance) || 0))
        .catch(err => console.error("Wallet Error", err));
    }
  }, [hotel, room, navigate, userId, searchDetails]);

  if (!hotel || !room) return null;

  // Price Calculation Logic
  const basePrice = room.base_price;
  const taxes = Math.round(basePrice * 0.12);
  const totalAmount = basePrice + taxes;
  const walletAmountToUse = useWallet ? Math.min(walletBalance, totalAmount) : 0;
  const finalPayableAmount = totalAmount - walletAmountToUse;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBooking = async () => {
    setIsProcessing(true);
    try {
      // CORRECT HOTEL ID USAGE:
      // We use the ID passed in searchDetails or the hotel object itself
      const finalHotelId = searchDetails.hotel_id || hotel.hotel_id || hotel.uuid;

      const orderData = {
        userId, 
        hotelId: finalHotelId, 
        roomId: room.room_id,
        sessionId, 
        customer, 
        searchDetails, 
        totalAmount,
        walletUsed: walletAmountToUse, 
        gatewayAmount: finalPayableAmount
      };

      console.log("orderData", orderData);

      // 1. Create Order
      const res = await axios.post(`${baseurl}/createBookingOrder`, orderData);

      console.log("res", res);


      if (res.data.status !== "success") throw new Error("Order creation failed");

      const { razorpayOrderId, bookingId } = res.data;

      // 2. Redirect Function
      const redirectToSuccess = (id) => {
        const successUrl = `${window.location.origin}/hotel-booking-success?booking=${id}`;
        window.location.href = successUrl;
      };

      // 3. Handle Full Wallet Payment
      if (finalPayableAmount <= 0) {
        const verifyRes = await axios.post(`${baseurl}/verifyBookingPayment`, { 
          bookingId, 
          paymentMode: "wallet" 
        });
        

        console.log("verifyRes", verifyRes);

        if (verifyRes.data.status === "success") {
          return redirectToSuccess(bookingId);
        }
        throw new Error("Wallet verification failed");
      }

      // 4. Handle Razorpay Payment
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) return alert("Razorpay SDK failed to load.");

      const options = {
        key: "rzp_test_A8i5qZiJOeIYx8", 
        amount: Math.round(finalPayableAmount * 100),
        currency: "INR",
        name: "GoStay",
        description: `Booking at ${hotel.hotel_name}`,
        order_id: razorpayOrderId,
        handler: async (response) => {
          const verifyData = {
            bookingId: bookingId,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            paymentMode: useWallet ? 'partial' : 'gateway',
            userId
          };


          console.log("verifyData", verifyData);


          try {
            const verifyRes = await axios.post(`${baseurl}/verifyBookingPayment`, verifyData);
            if (verifyRes.data.status === "success") {
              redirectToSuccess(bookingId);
            } else {
              alert("Payment failed on server. Please check your account.");
              setIsProcessing(false);
            }
          } catch (err) {
            console.error("Verification Error:", err);
            alert("Verification error. Do not refresh, contact support.");
            setIsProcessing(false);
          }
        },
        prefill: { 
          name: customer.name, 
          email: customer.email, 
          contact: customer.phone 
        },
        theme: { color: "#2276E3" },
        modal: {
          ondismiss: () => setIsProcessing(false)
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Booking Error:", error);
      alert(error.message || "Something went wrong.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="ck-page">
      <nav className="ck-nav">
        <div className="ck-container">
          <button onClick={() => navigate(-1)} className="ck-back"><FaArrowLeft /> Review Booking</button>
        </div>
      </nav>

      <div className="ck-container ck-layout">
        <div className="ck-main">
          
          {/* Hotel Info Card */}
          <div className="ck-card ck-hotel-card">
            <div className="ck-hotel-flex">
              <img src={`${imgurl}/${hotel.images[0]}`} alt="hotel" className="ck-img" />
              <div className="ck-info">
                <div className="ck-stars">{hotel.star_rating} <FaStar /></div>
                <h3>{hotel.hotel_name}</h3>
                <p className="ck-loc"><FaMapMarkerAlt /> {hotel.hotel_address}, {hotel.city_village}</p>
              </div>
            </div>
            <div className="ck-dates">
              <div className="ck-date-box">
                <small>Check-in</small>
                <p>{new Date(searchDetails.checkIn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              </div>
              <FaChevronRight className="ck-arr" />
              <div className="ck-date-box">
                <small>Check-out</small>
                <p>{new Date(searchDetails.checkOut).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              </div>
              <div className="ck-guest-tag">
                {searchDetails.rooms} Room • {Number(searchDetails.adults) + Number(searchDetails.children)} Guests
              </div>
            </div>
          </div>

          {/* Wallet Card */}
          <div className={`ck-card ck-wallet ${walletBalance > 0 ? 'active' : ''}`}>
            <div className="ck-wallet-top">
              <div className="ck-icon-circle"><FaWallet /></div>
              <div className="ck-wallet-text">
                <h4>GoWallet Balance</h4>
                <p>Available: <span>₹{walletBalance.toFixed(0)}</span></p>
              </div>
              <label className="ck-switch">
                <input type="checkbox" checked={useWallet} onChange={() => setUseWallet(!useWallet)} disabled={walletBalance <= 0} />
                <span className="ck-slider"></span>
              </label>
            </div>
            {useWallet && <div className="ck-wallet-applied">Applied: -₹{walletAmountToUse}</div>}
          </div>

          {/* Guest Details */}
          <div className="ck-card">
            <h4 className="ck-section-title"><FaUser /> Traveller Details</h4>
            <div className="ck-traveller-info">
              <p><strong>{customer.name}</strong></p>
              <p>{customer.email} • {customer.phone}</p>
            </div>
          </div>
        </div>

        {/* Sidebar Price Summary */}
        <aside className="ck-side">
          <div className="ck-price-card">
            <h4>Price Summary</h4>
            <div className="ck-row"><span>Base Price</span><span>₹{basePrice}</span></div>
            <div className="ck-row"><span>Taxes & Service Fees (12%)</span><span>₹{taxes}</span></div>
            {useWallet && <div className="ck-row ck-save"><span>Wallet Applied</span><span>-₹{walletAmountToUse}</span></div>}
            <div className="ck-total">
              <span>Amount to Pay</span>
              <span>₹{finalPayableAmount}</span>
            </div>
            <button className="ck-pay-btn" onClick={handleBooking} disabled={isProcessing}>
              {isProcessing ? "Processing..." : `Pay ₹${finalPayableAmount}`}
            </button>
            <div className="ck-secure"><FaShieldAlt /> 100% Secure Payment</div>
          </div>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      <div className="ck-mobile-footer">
        <div className="ck-m-price">
          <small>Total Payable</small>
          <span>₹{finalPayableAmount}</span>
        </div>
        <button className="ck-m-btn" onClick={handleBooking} disabled={isProcessing}>
          {isProcessing ? "..." : "Proceed to Pay"}
        </button>
      </div>
    </div>
  );
};

export default Checkout;